import { useState, type ReactNode } from 'react';
import { Modal, Spinner } from '../components';
import { useI18n } from '../i18n';
import { describe } from '../errors';
import type { Removal } from '../api-modules';

/**
 * Asking before removing something, and reporting what actually happened.
 *
 * Three things this exists to get right, all of which were wrong when delete
 * did not exist at all and would have been easy to get wrong when adding it:
 *
 *   1. The server decides between DELETING and DEACTIVATING, based on whether
 *      history points at the record. So this cannot promise a deletion in
 *      advance — it says what will be attempted, and afterwards says which of
 *      the two happened and why. A supplier reported as deleted when it was
 *      retired is how people lose track of their own data.
 *   2. A refusal is information, not a failure. "This has 4 purchases" tells
 *      the user what to do next; "Error" does not.
 *   3. Irreversible actions ask for the word, not just a click. Only where the
 *      action really is irreversible — an archive is a click, because undoing
 *      it is also a click.
 */
export function ConfirmRemove({
  title,
  what,
  body,
  danger = true,
  confirmWord,
  actionLabel,
  onConfirm,
  onClose,
  onDone,
}: {
  title: string;
  /** The thing being removed, shown so the user can check they have the right one. */
  what: string;
  body?: ReactNode;
  danger?: boolean;
  /** When set, the user must type this before the button enables. */
  confirmWord?: string;
  actionLabel?: string;
  onConfirm: () => Promise<Removal | { deleted: boolean } | void>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [outcome, setOutcome] = useState<Removal | null>(null);

  const armed = !confirmWord || typed.trim().toLowerCase() === confirmWord.toLowerCase();

  // Once the server has answered with "deactivated instead", the dialog stops
  // being a question and becomes the answer.
  if (outcome) {
    return (
      <Modal
        title={title}
        onClose={onDone}
        footer={
          <button type="button" className="btn" onClick={onDone}>
            {t.common.close}
          </button>
        }
      >
        <div className="stack">
          <div className="notice">
            <span>
              <strong>{what}</strong>
              <span style={{ display: 'block', marginTop: 4 }}>
                {outcome.deleted ? t.remove.wasDeleted : t.remove.wasDeactivated}
              </span>
            </span>
          </div>
          {!outcome.deleted && outcome.reason ? (
            <p style={{ color: 'var(--ink-3)', margin: 0, fontSize: 13 }}>
              {t.remove.keptBecause}{' '}
              {Object.entries(outcome.reason)
                .map(([label, n]) => `${n} ${label}`)
                .join(', ')}
              .
            </p>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn'}
            disabled={busy || !armed}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const result = await onConfirm();
                // A plain `{ deleted: true }` is normalised so the summary
                // below has one shape to render.
                if (result && typeof result === 'object' && 'deactivated' in result) {
                  setOutcome(result);
                } else {
                  onDone();
                }
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : (actionLabel ?? t.remove.confirm)}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}

        <p style={{ margin: 0 }}>
          <strong>{what}</strong>
        </p>
        {body}

        {confirmWord ? (
          <label className="stack" style={{ gap: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {t.remove.typeToConfirm.replace('{word}', confirmWord)}
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              autoFocus
            />
          </label>
        ) : null}
      </div>
    </Modal>
  );
}
